import type {
	MultipleQueriesResponse,
	SearchForFacetValuesResponse,
	SearchResponse,
} from '@algolia/client-search'

import { AUTOCOMPLETE_CONFIG } from '@/constants/autocomplete'
import * as catalogueIds from '@/constants/catalogueIds'
import type {
	AlgoliaCollection,
	AlgoliaPlaylist,
	CollectionHit,
	FacetHit,
	PlaylistHit,
	QueryHit,
} from '@/utils/algolia/autocompleteHitTypes'
import { filterCollectionsByUserCatalogues } from '@/utils/filterCollectionsByUserCatalogues'

import type { AutocompleteQueryDescriptor } from '../types'
import type { AutocompleteResult } from '../types'
import { getFacetSectionKey } from '../utils'

type MappedAutocompleteResponses = {
	autocompleteResult: AutocompleteResult
	sectionMappingErrors: Array<{
		section: string
		error: Error
	}>
}

type AutocompleteSearchResponse = MultipleQueriesResponse<
	QueryHit | AlgoliaPlaylist | AlgoliaCollection
>['results'][number]

const getSearchResponse = (
	response: AutocompleteSearchResponse | undefined,
	section: string
): SearchResponse<QueryHit | AlgoliaPlaylist | AlgoliaCollection> => {
	if (
		typeof response === 'object' &&
		response !== null &&
		'hits' in response &&
		Array.isArray(response.hits)
	) {
		return response
	}

	throw new Error(`expected search hits for ${section}`)
}

const getSuggestionHits = (
	response: AutocompleteSearchResponse | undefined
): QueryHit[] =>
	getSearchResponse(response, 'query suggestions').hits as QueryHit[]

const getPlaylistHits = (
	response: AutocompleteSearchResponse | undefined
): AlgoliaPlaylist[] =>
	getSearchResponse(response, 'playlists').hits as AlgoliaPlaylist[]

const getCollectionHits = (
	response: AutocompleteSearchResponse | undefined
): AlgoliaCollection[] =>
	getSearchResponse(response, 'collections').hits as AlgoliaCollection[]

const getFacetValues = (
	response: AutocompleteSearchResponse | undefined,
	section: string
): SearchForFacetValuesResponse => {
	if (
		typeof response === 'object' &&
		response !== null &&
		'facetHits' in response &&
		Array.isArray(response.facetHits)
	) {
		return response
	}

	throw new Error(`expected facet values response for ${section}`)
}

const getFirstAvailableTitle = (
	record: Record<string, unknown>,
	searchableTitleAttributes: string[]
): string => {
	for (const titleField of searchableTitleAttributes) {
		const title = record[titleField]

		if (typeof title === 'string' && title.trim() !== '') {
			return title
		}
	}

	return ''
}

const mapFacetHits = (
	response: SearchForFacetValuesResponse,
	facetName: string
): FacetHit[] =>
	response.facetHits.map((hit) => ({
		objectID: `${hit.value}-facet`,
		facetType: facetName,
		label: hit.value,
		count: hit.count,
		_highlightResult: {
			label: {
				value: hit.highlighted ?? hit.value,
				matchLevel: 'full' as const,
			},
		},
	}))

const mapPlaylistHit = (
	playlist: AlgoliaPlaylist,
	searchableTitleAttributes: string[]
): PlaylistHit => ({
	objectID: playlist.objectID,
	url: `/playlist/${playlist.urlSlug}`,
	value: getFirstAvailableTitle(playlist, searchableTitleAttributes),
	imageUrl: playlist.thumbnail?.url ?? '',
	availableTitles: searchableTitleAttributes,
	_highlightResult: playlist._highlightResult,
	title_en: playlist.title_en,
})

const getVisibleCollections = (
	collections: AlgoliaCollection[],
	user: User | null
): AlgoliaCollection[] => {
	if (user) {
		return filterCollectionsByUserCatalogues(collections, user)
	}

	return collections.filter((collection) =>
		collection.catalogue?.some((catalogueName) => {
			const catalogueId =
				catalogueIds.Catalogue[
					catalogueName as keyof typeof catalogueIds.Catalogue
				]

			return (
				catalogueId !== undefined &&
				catalogueIds.defaultCatalogues.includes(catalogueId)
			)
		})
	)
}

const mapCollectionHit = (
	collection: AlgoliaCollection,
	searchableTitleAttributes: string[]
): CollectionHit => ({
	objectID: collection.objectID,
	url: `/collections/${collection.urlSlug}`,
	value: getFirstAvailableTitle(collection, searchableTitleAttributes),
	imageUrl: collection.thumbnail?.url ?? '',
	availableTitles: searchableTitleAttributes,
	_highlightResult: collection._highlightResult,
	title_en: collection.title_en,
})

export const mapAutocompleteResults = (
	searchResponses: MultipleQueriesResponse<
		QueryHit | AlgoliaPlaylist | AlgoliaCollection
	>['results'],
	requestDescriptors: AutocompleteQueryDescriptor[],
	searchableTitleAttributes: string[],
	user: User | null
): MappedAutocompleteResponses => {
	const autocompleteResult: AutocompleteResult = {
		suggestionHits: [],
		facetHits: [],
		playlistHits: [],
		collectionHits: [],
	}
	const sectionMappingErrors: MappedAutocompleteResponses['sectionMappingErrors'] =
		[]

	for (const [index, descriptor] of requestDescriptors.entries()) {
		try {
			const response = searchResponses[index]

			switch (descriptor.section.kind) {
				case 'suggestions':
					autocompleteResult.suggestionHits = getSuggestionHits(response)
					break
				case 'facet':
					autocompleteResult.facetHits.push(
						...mapFacetHits(
							getFacetValues(
								response,
								getFacetSectionKey(descriptor.section.facetName)
							),
							descriptor.section.facetName
						)
					)
					break
				case 'playlists':
					autocompleteResult.playlistHits = getPlaylistHits(response).map(
						(playlist) => mapPlaylistHit(playlist, searchableTitleAttributes)
					)
					break
				case 'collections':
					autocompleteResult.collectionHits = getVisibleCollections(
						getCollectionHits(response),
						user
					).map((collection) =>
						mapCollectionHit(collection, searchableTitleAttributes)
					)
					break
			}
		} catch (error) {
			sectionMappingErrors.push({
				section:
					descriptor.section.kind === 'facet'
						? getFacetSectionKey(descriptor.section.facetName)
						: descriptor.section.kind,
				error:
					error instanceof Error
						? error
						: new Error('unknown section mapping error'),
			})
		}
	}

	autocompleteResult.facetHits = autocompleteResult.facetHits
		.sort((left, right) => right.count - left.count)
		.slice(0, AUTOCOMPLETE_CONFIG.HITS_PER_PAGE.FACETS)

	return { autocompleteResult, sectionMappingErrors }
}
